import { MapPin } from 'lucide-react'
import type { Lang } from '../../../lib/i18n'
import type { OrderDetail } from '../../../lib/orders-api'
import { WRITE_ROLES } from '../../../lib/auth'
import { Button } from '../../../components/ui/button'
import { FeeChangeDialog } from '../FeeChangeDialog'
import { Ltr } from '../Ltr'
import { formatDate, formatMoney } from '../format'
import { ot } from '../orders-i18n'
import { SectionCard } from './SectionCard'

interface Props {
  order: OrderDetail
  lang: Lang
  roles: string[]
  isOnline: boolean
  onFeeChanged: () => void
}

export function DeliverySection({ order, lang, roles, isOnline, onFeeChanged }: Props) {
  if (order.fulfillmentMethod === 'pickup') {
    return (
      <SectionCard title={ot(lang, 'deliveryTitle')} lang={lang}>
        <p className="text-sm text-muted-foreground">{ot(lang, 'noDeliveryInfo')}</p>
      </SectionCard>
    )
  }

  // M3 role model (same as HeaderSection) — write-capability = admin/owner/hop.
  const canWrite = roles.some((r) => (WRITE_ROLES as readonly string[]).includes(r))
  // R562: the server's flag, never a client copy of its status rule.
  const canChangeFee = canWrite && order.feeCorrectable === true

  const cityName = lang === 'ar' ? order.deliveryLocation?.cityNameAr : order.deliveryLocation?.cityNameEn
  const zoneName = lang === 'ar' ? order.deliveryZone?.nameAr : order.deliveryZone?.nameEn

  return (
    <SectionCard title={ot(lang, 'deliveryTitle')} lang={lang}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{ot(lang, 'street')}</div>
            <div className="text-sm text-foreground">
              {order.streetName || '—'}
              {order.buildingFloor && <span className="text-muted-foreground"> · {order.buildingFloor}</span>}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{ot(lang, 'city')} / {ot(lang, 'zone')}</div>
            <div className="text-sm text-foreground">{cityName || '—'}{zoneName ? ` · ${zoneName}` : ''}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{ot(lang, 'deliveryDate')} / {ot(lang, 'deliverySlot')}</div>
            <div className="text-sm text-foreground">
              <Ltr>{formatDate(order.deliveryDate)}</Ltr>
              {order.deliverySlot && <span> · <Ltr>{order.deliverySlot}</Ltr></span>}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{ot(lang, 'deliveryFee')}</div>
            <div className="text-sm text-foreground">
              <Ltr>{formatMoney(order.appliedDeliveryFee, order.currency)}</Ltr>
              {order.originalDeliveryFee && order.originalDeliveryFee !== order.appliedDeliveryFee && (
                <span className="text-muted-foreground">
                  {' '}(<Ltr>{ot(lang, 'originalFee')}: {formatMoney(order.originalDeliveryFee, order.currency)}</Ltr>)
                </span>
              )}
            </div>
          </div>
        </div>

        {canChangeFee && (
          <FeeChangeDialog
            trigger={
              <Button variant="outline" size="sm">
                {ot(lang, 'changeDeliveryFee')}
              </Button>
            }
            order={order}
            lang={lang}
            isOnline={isOnline}
            onSuccess={onFeeChanged}
          />
        )}

        {/* Landmark/directions get real visual weight — Lebanese addresses are landmark-based,
            not street-address-based, so delivery_notes is the operationally load-bearing field. */}
        {order.deliveryNotes && (
          <div className="flex items-start gap-2.5 rounded-lg border border-navy/20 bg-navy-tint px-4 py-3">
            <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-navy" />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-navy">{ot(lang, 'deliveryNotes')}</div>
              <p className="text-sm font-medium text-foreground"><bdi>{order.deliveryNotes}</bdi></p>
            </div>
          </div>
        )}

        {order.giftMessage && (
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">{ot(lang, 'giftMessage')}</div>
            <p className="text-sm italic text-foreground"><bdi>{order.giftMessage}</bdi></p>
          </div>
        )}
      </div>
    </SectionCard>
  )
}
